import type { CommitmentInternalTransferReceiverVersion } from '@festina/life-general-types/life'
import {
  type Compare,
  listUtils,
  notEmpty,
  stringUtils,
} from '@festina-life/core'
import { useTranslate, useTranslateEnum } from '@festina-life/ui/hooks'
import {
  createColumnDefiner,
  TableWithCompare,
} from '@festina-life/ui/listsAndTables'
import { CardBody } from '@festina-life/ui/structure'
import { type FC, useMemo } from 'react'

import { useAmountFormatter } from '../../hooks/useAmountFormatter'
import { useCommitmentVersion } from '../../hooks/useCommitmentVersion'
import { useHook } from '../../lenteHooks'
import { withLifeErrorBoundary } from '../../libs/withLifeErrorBoundary'
import { LifeCard } from '../card/LifeCard'
import { aggregateDataPerClaimSender } from './commitmentDetailCardsUtility'
import { DeathClaimsNameColumn } from './DeathClaimsNameColumn'

const DEATH_EVENTS = ['death', 'deathOfRelated'] as const

const defineColumn =
  createColumnDefiner<CommitmentInternalTransferReceiverVersion>()

const DeathClaimsReceiverCardInternal: FC = () => {
  const {
    currentCommitmentVersion: commitmentVersion,
    previousCommitmentVersion,
    isDiffing,
  } = useCommitmentVersion()

  const translate = useTranslate()
  const translateEnum = useTranslateEnum()
  const { format } = useAmountFormatter()

  const internalIdMap = useMemo(() => {
    const secondaryIds = (
      commitmentVersion?.commitmentInternalTransferReceiverVersions ?? []
    )
      .map((x) => x?.fromCommitmentSecondaryId)
      .filter(notEmpty)
    const prevSecondaryIds = (
      previousCommitmentVersion?.commitmentInternalTransferReceiverVersions ??
      []
    )
      .map((x) => x?.fromCommitmentSecondaryId)
      .filter(notEmpty)

    return secondaryIds.concat(prevSecondaryIds)
  }, [
    commitmentVersion?.commitmentInternalTransferReceiverVersions,
    previousCommitmentVersion?.commitmentInternalTransferReceiverVersions,
  ])

  const entityNameMap = useHook('pages.crm.useResolveIdToEntityName')(
    internalIdMap,
  )

  const pairs: Compare<CommitmentInternalTransferReceiverVersion>[] =
    listUtils.getListOfPairs({
      currentList: aggregateDataPerClaimSender(
        (commitmentVersion?.commitmentInternalTransferReceiverVersions ?? [])
          .filter(notEmpty)
          .filter((transfer) =>
            stringUtils.strictIncludes(
              transfer.lifeInsuranceEvent,
              DEATH_EVENTS,
            ),
          ),
      ),

      prevList: aggregateDataPerClaimSender(
        (
          previousCommitmentVersion?.commitmentInternalTransferReceiverVersions ??
          []
        )
          .filter(notEmpty)
          .filter((transfer) =>
            stringUtils.strictIncludes(
              transfer.lifeInsuranceEvent,
              DEATH_EVENTS,
            ),
          ),
      ),
      keyFunc: (x) =>
        `${x.commandId}${x.relationshipType}${x.fromCoverKey}${x.fromPolicyKey}`,
    })

  const columns = {
    policy: defineColumn({
      heading: translate('deathClaims.policy'),
      keyPath: 'fromPolicyKey',
    }),
    cover: defineColumn({
      heading: translate('deathClaims.covers'),
      keyPath: 'fromCoverKey',
    }),
    type: defineColumn({
      heading: translate('deathClaims.relationshipType.receiver'),
      keyPath: 'relationshipType',
      formatter: (s) => translateEnum('life.enum.RelationshipType', s),
    }),
    name: defineColumn({
      heading: translate('deathClaims.senderName'),
      keyPath: 'fromCommitmentSecondaryId',
      formatter: (fromCommitmentSecondaryId) =>
        fromCommitmentSecondaryId && (
          <DeathClaimsNameColumn
            secondaryId={fromCommitmentSecondaryId}
            name={entityNameMap[fromCommitmentSecondaryId].name}
            type={entityNameMap[fromCommitmentSecondaryId].type}
          />
        ),
      sortable: false,
    }),
    benefit: defineColumn({
      heading: translate('deathClaims.benefit'),
      keyPath: 'benefit',
      formatter: format,
      alignment: 'right',
    }),
  }

  const deadStatuses = ['death', 'deathRegistered'] as const
  const isEitherCommitmentAlive =
    !stringUtils.strictIncludes(
      commitmentVersion?.commitmentStatus,
      deadStatuses,
    ) ||
    !stringUtils.strictIncludes(
      previousCommitmentVersion?.commitmentStatus,
      deadStatuses,
    )

  return (
    <>
      {isEitherCommitmentAlive && !!pairs.length && (
        <LifeCard
          className="col-span-2"
          headerProps={{ title: translate('deathClaimsCard.title') }}
        >
          <CardBody outerClassName="max-h-72">
            <TableWithCompare
              columns={columns}
              rowPairs={pairs}
              isDiffing={isDiffing}
            />
          </CardBody>
        </LifeCard>
      )}
    </>
  )
}

export const DeathClaimsReceiverCard = withLifeErrorBoundary(
  DeathClaimsReceiverCardInternal,
)
